<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="isLoading"
    button-location="top"
    loading-message="Loading Metrics Data..."
    @submit="loadOperationMetricsData"
  >
    <template #title>
      Operation Metrics
    </template>

    <template #top-form>
      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Date Range (EST)* </label>
        <div class="col-sm-6">
          <date-picker
            v-model="timeSpanValue"
            required
            type="daterange"
          >
            <template #comment>
              <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
            </template>
          </date-picker>
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Call Type </label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="callType"
            :options="callTypeList"
          />
        </div>
      </div>

      <div class="form-submit-button-group mb-20">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="isLoading"
        >
          Load
        </p-button>

        <p-button
          type="info"
          round
          :is-disable="!isDownloadable"
          @click="downloadData"
        >
          Download
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>
    </template>

    <template #content>
      <div class="row justify-content-center">
        <el-switch
          v-model="viewMode"
          active-color="#13ce66"
          inactive-color="#13ce66"
          inactive-text="Average Handle Times"
          inactive-value="handleTime"
          active-text="Average Waiting Times"
          active-value="waitingTime"
        />
      </div>

      <table-view-container
        v-if="tableColumns.length !== 0"
        :page-id="pageId"
        :data="viewMode === 'handleTime' ? averageHandleTimes : averageWaitTimes"
        :table-columns="tableColumns"
        :custom-cell-style-func="tableCustomCellStyle"
        :fixed-column-counts="1"
        :is-summary="true"
        :get-summaries="getAverageSummaries"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, watch, computed } from 'vue';
import { groupBy, cloneDeep } from 'lodash';
import Moment from 'moment-timezone';
import { Parser } from '@json2csv/plainjs';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { OperationMetric } from '@/models/statistic';
import { TableColumnType } from '@/models/table';
import { statisticsService } from '@/services/statistics';

type HourlyAverages = {
  hour?: string;
  [date: string]: string | undefined; // e.g., '2025-07-16': '5.32'
};

const pageId = 'operation-metrics';
const callTypeList = [
  {
    key: '',
    label: 'All',
  },
  {
    key: 'ride',
    label: 'Ride',
  },
  {
    key: 'gourmet',
    label: 'Gourmet',
  },
  {
    key: 'grocery',
    label: 'Grocery',
  },
  {
    key: 'registration',
    label: 'Registration',
  },
  {
    key: 'supervisor',
    label: 'Supervisor',
  },
  {
    key: 'outbound',
    label: 'Outbound',
  },
];

const { showWarningPopupDialog, showErrorPopupDialog } = useSwalDialog();

const timeSpanValue: Ref<any> = ref(null);
const startDate = ref('');
const endDate = ref('');
const isLoading = ref(false);
const viewMode = ref('handleTime');
const callType = ref('');

const averageHandleTimes: Ref<HourlyAverages[]> = ref([]);
const averageWaitTimes: Ref<HourlyAverages[]> = ref([]);
const tableColumns: Ref<TableColumnType[]> = ref([]);
const summaryColumns: Ref<string[]> = ref([]);
const summaryData: Ref<HourlyAverages> = ref({});

const isDownloadable = computed(() => {
  return (
    (viewMode.value === 'handleTime' && averageHandleTimes.value.length > 0) ||
    (viewMode.value === 'waitingTime' && averageWaitTimes.value.length > 0)
  );
});

const loadOperationMetricsData = async () => {
  averageWaitTimes.value = [];
  averageHandleTimes.value = [];
  tableColumns.value = [];
  summaryColumns.value = [];
  summaryData.value = {};
  isLoading.value = true;

  try {
    const response = await statisticsService.fetchOperationMetrics({
      startDate: startDate.value,
      endDate: endDate.value,
      type: callType.value,
    });

    initTableColumns();
    parseData(response);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  isLoading.value = false;
};

const initTableColumns = () => {
  tableColumns.value = [
    {
      label: 'Time (EST)',
      field: 'hour',
      width: 110,
      isDisableSort: true,
    },
  ];

  let startDateMoment = Moment(startDate.value);
  const endDateMoment = Moment(endDate.value);

  while (startDateMoment.isBefore(endDateMoment)) {
    tableColumns.value.push({
      label: startDateMoment.format('YYYY-MM-DD (ddd)'),
      field: startDateMoment.format('YYYY-MM-DD'),
      width: 120,
      isDisableSort: true,
    });

    summaryColumns.value.push(startDateMoment.format('YYYY-MM-DD'));

    startDateMoment = startDateMoment.add(1, 'day');
  }
};

const parseData = (data: OperationMetric[]) => {
  averageHandleTimes.value = [];
  averageWaitTimes.value = [];

  const groupedDataByHour = groupBy(data, (item: OperationMetric) => item.hour);
  for (const [hour, hourData] of Object.entries(groupedDataByHour)) {
    const formatedHour = Moment()
      .set({
        hour,
        minute: 0,
      })
      .format('hh:mm A');

    averageHandleTimes.value[Number(hour)] = {
      hour: formatedHour,
    };
    averageWaitTimes.value[Number(hour)] = {
      hour: formatedHour,
    };

    const groupedDataByDate = groupBy(hourData, (item: OperationMetric) => item.date);
    for (const [date, dateData] of Object.entries(groupedDataByDate)) {
      const handleTimesSum = dateData.reduce((sum, curVal) => {
        return sum + (curVal.handleTime ?? 0);
      }, 0);

      const waitingTimesSum = dateData.reduce((sum, curVal) => {
        return sum + (curVal.waitTime ?? 0);
      }, 0);

      averageHandleTimes.value[Number(hour)][date] = (handleTimesSum / dateData.length / 60).toFixed(2);
      averageWaitTimes.value[Number(hour)][date] = (waitingTimesSum / dateData.length / 60).toFixed(2);
    }
  }
};

const tableCustomCellStyle = (param: any) => {
  const { column, row } = param;

  // no need to set background of label
  if (column.property === 'hour') {
    return null;
  }

  // if value does not exist, no need to set background
  if (!row[column.property]) {
    return null;
  }

  const normalizedCellValue = Number((row[column.property] * 1.5) / 100);
  const opacity = Math.max(normalizedCellValue, 0);

  return {
    'background-color': `rgba(228, 37, 6, ${opacity}) !important`,
  };
};

// el-table summary reference - https://element-plus.org/en-US/component/table.html#summary-row
const getAverageSummaries = (param: any) => {
  const { columns, data } = param;
  const averageSummary: string[] = [];

  /*
    example of data
    [
      {hour: '12:00 AM', 2023-05-12: '5.65', 2023-05-13: 6.58'},
      {hour: '01:00 AM', 2023-05-12: '6.65', 2023-05-13: 6.88'},
      {hour: '02:00 AM', 2023-05-12: '7.95', 2023-05-13: 7.18'},
      .......
      {hour: '09:00 PM', 2023-05-12: '6.45', 2023-05-13: 8.09'},
      {hour: '10:00 PM', 2023-05-12: '4.51'},
      {hour: '11:00 PM', 2023-05-12: '5.24'},
    ]

    Total row count will be 24, because we already group the operation metrics by hour
  */
  columns.forEach((column: any, index: number) => {
    // The 1st column of the table is hour field and in the summary section, the 1st column should be label
    // No need to calculate the average for the 1st column
    if (index === 0) {
      averageSummary[index] = 'Average';

      // it is needed to include summary data to download
      summaryData.value[tableColumns.value[index].field] = 'Average';
      return;
    }

    // column.property is the field name of the data in the table
    // in above case, column date will be 2023-05-12, 2023-05-13
    const columnDate = column.property;

    // Convert string value to number or add missing data
    // As you can see the example data, last column can miss metric data(ex:current date is 2023-05-13 and current time is 09:20 PM)
    const values = data.map((item: any) => (item[columnDate] && Number(item[columnDate])) || 0);

    let metricsCountInColumnData = 0;
    const sum = values.reduce((prevSum: number, currentValue: number) => {
      // if metric data is zero, it means we add the value (0) manually and no need to calculate the count.
      // The max value of count is 24, but it can be less than 24 (ex:current date is 2023-05-13 and current time is 09:20 PM)
      if (currentValue) {
        metricsCountInColumnData++;
        return prevSum + currentValue;
      } else {
        return prevSum;
      }
    }, 0);

    const average = metricsCountInColumnData ? (sum / metricsCountInColumnData).toFixed(2) : '-';
    averageSummary[index] = average;

    // it is needed to include summary data to download
    summaryData.value[tableColumns.value[index].field] = averageSummary[index];
  });

  /*
    example data of average summary based on example data
    ['Average', 7.13, 7.44, 6.91]

    The length of the averageSummary should be same as table column count
  */
  return averageSummary;
};

const downloadData = () => {
  const data = cloneDeep(viewMode.value === 'handleTime' ? averageHandleTimes.value : averageWaitTimes.value);
  data.push(summaryData.value); // Add summar data into csv

  const fields = tableColumns.value.map((item: TableColumnType) => ({
    label: item.label,
    value: item.field,
  }));

  const fileName = `Average ${viewMode.value === 'handleTime' ? 'Handle' : 'Waiting'} Time(${util.formatDateTime(
    startDate.value,
  )} ~ ${util.formatDateTime(endDate.value)}).csv`;
  const parser = new Parser({ fields });
  const csv = parser.parse(data);
  util.downloadJSONToCSV(csv, fileName);
};

const resetForm = () => {
  timeSpanValue.value = null;
  tableColumns.value = [];
  averageHandleTimes.value = [];
  averageWaitTimes.value = [];
};

watch(timeSpanValue, (value: any) => {
  if (value) {
    /*
      value[0] comes in as local time as start of day
      value[1] comes in as local time as end of day
      the below comments are with value[0] = 2021-10-01 and value[1] = 2021-10-31
    */

    const date1MomentUtc = Moment(value[0]).utc(); // 2021-09-30T17:00:00Z
    const date2MomentUtc = Moment(value[1]).utc(); // 2021-10-31T16:59:59Z

    const dateTimeSpanMilliseconds = date2MomentUtc.diff(date1MomentUtc); // 2678399000
    const duration = Moment.duration(dateTimeSpanMilliseconds);
    const durationDays = duration.asDays(); // 30.999988425925928

    if (durationDays > 31) {
      showWarningPopupDialog({
        text: "The start date and end dates can only be 31 days max or else it'll crash our database",
      });
      timeSpanValue.value = null;
      startDate.value = '';
      endDate.value = '';
      return;
    }

    const date1Moment = Moment(value[0]); // 2021-10-01T00:00:00+07:00
    const date2Moment = Moment(value[1]); // 2021-10-31T23:59:59+07:00

    startDate.value = util.formatDateTime(date1Moment, 'YYYY-MM-DD HH:mm:ss'); // 2021-10-01 00:00:00
    endDate.value = util.formatDateTime(date2Moment, 'YYYY-MM-DD HH:mm:ss'); // 2021-10-31 23:59:59
  } else {
    startDate.value = '';
    endDate.value = '';
  }
});
</script>

<style lang="scss">
.operation-metrics {
  .el-switch {
    .el-switch__label {
      font-weight: bold;
      width: 160px;
    }
  }
}
</style>
